import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action, upload } from '../lib/api.ts';
import { day, money } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * ESG: the carbon footprint by scope, worked out from activities and the business's own
 * emission factors, with reduction targets and the yearly social and governance figures.
 */

type Scope = '1' | '2' | '3';
const SCOPES: Scope[] = ['1', '2', '3'];
const CATEGORIES = ['fuel', 'electricity', 'heating', 'refrigerants', 'travel', 'commuting', 'freight', 'goods', 'waste', 'water', 'other'] as const;
type Category = (typeof CATEGORIES)[number];
const DEFAULT_SCOPE: Record<Category, Scope> = {
  fuel: '1',
  heating: '1',
  refrigerants: '1',
  electricity: '2',
  travel: '3',
  commuting: '3',
  freight: '3',
  goods: '3',
  waste: '3',
  water: '3',
  other: '3',
};
const CAT_ICON: Record<Category, IconName> = {
  fuel: 'car',
  electricity: 'flame',
  heating: 'flame',
  refrigerants: 'sun',
  travel: 'globe',
  commuting: 'bike',
  freight: 'truck',
  goods: 'cart',
  waste: 'trash',
  water: 'circle',
  other: 'dots',
};
type TargetScope = 'all' | '1' | '2' | '3' | '12';
const TARGET_SCOPES: TargetScope[] = ['all', '12', '1', '2', '3'];
type Pillar = 'environment' | 'social' | 'governance';

interface Overview {
  year: number;
  today: string;
  years: number[];
  total: number;
  scopes: Record<Scope, number>;
  categories: Partial<Record<Category, { grams: number; scope: Scope }>>;
  months: ({ month: number } & Record<Scope, number>)[];
  count: number;
  previous: number;
  factors: number;
  intensity: { per_person: number | null; per_million: number | null; revenue: number | null; headcount: number | null };
  fraction: number;
  targets: (Target & { base: number; current: number; projected: number | null; goal: number; path: number; achieved: number | null; on_track: boolean | null })[];
  metrics: { filled: number; total: number };
  fleet: boolean;
}
interface Factor {
  id: string;
  name: string;
  category: Category;
  scope: Scope;
  unit: string;
  mg_per_unit: number;
  source: string | null;
  active: boolean;
  used: number;
}
interface ActivityRow {
  id: string;
  date: string;
  factor: string;
  quantity: number;
  mg_per_unit: number;
  grams: number;
  note: string | null;
  source: 'manual' | 'import' | 'fleet';
  factor_name: string;
  unit: string;
  scope: Scope;
  category: Category;
  by_name: string | null;
}
interface Target {
  id: string;
  name: string;
  scope: TargetScope;
  base_year: number;
  target_year: number;
  reduction: number;
}
interface Metric {
  key: string;
  pillar: Pillar;
  unit: string;
  value: number | null;
  note: string | null;
  previous: number | null;
}

// ---- formatting -----------------------------------------------------------------------------

const num = (n: number, max = 2) => new Intl.NumberFormat(locale(), { maximumFractionDigits: max }).format(n);
/** Grams of CO2e as people read them: "850 kg", "12.4 t". */
export function co2(grams: number): string {
  const abs = Math.abs(grams);
  if (abs >= 1_000_000) return t('esg.tonnes', { n: num(grams / 1_000_000, abs >= 100_000_000 ? 0 : abs >= 10_000_000 ? 1 : 2) });
  return t('esg.kg', { n: num(grams / 1000, abs >= 10_000 ? 0 : 1) });
}
const qtyText = (thousandths: number) => num(thousandths / 1000, 3);
/** "12,5" or "1 200.5" → thousandths (or millionths with digits = 6); null when it is not a number. */
function parseDecimal(text: string, digits = 3): number | null {
  const clean = text.replace(/[\s  ]/g, '').replace(',', '.');
  if (!new RegExp(`^\\d+(\\.\\d{1,${digits}})?$`).test(clean)) return null;
  const n = Math.round(Number(clean) * 10 ** digits);
  return Number.isSafeInteger(n) ? n : null;
}
const scopeLabel = (s: Scope) => t(`esg.scope_${s}` as MessageKey);
const catLabel = (c: Category) => t(`esg.cat_${c}` as MessageKey);
const pct = (part: number, whole: number) => (whole ? Math.round((part / whole) * 100) : 0);

function ScopeChip({ scope }: { scope: Scope }) {
  return <span className={`es-chip es-s${scope}`}>{t('esg.scope_n', { n: scope })}</span>;
}

// ---- the page -------------------------------------------------------------------------------

type Tab = 'overview' | 'activities' | 'factors' | 'targets' | 'figures';

export function Esg() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const manage = can('esg.manage');
  const year = Number(params.get('year')) || null;
  const { data: o, reload } = useAction<Overview>('esg.overview', { year });
  const tabs: { key: Tab; icon: IconName }[] = [
    { key: 'overview', icon: 'chart' },
    { key: 'activities', icon: 'list' },
    { key: 'factors', icon: 'sliders' },
    { key: 'targets', icon: 'flame' },
    { key: 'figures', icon: 'users' },
  ];
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'overview') as Tab;
  const set = (k: 'tab' | 'year', v: string | null) => {
    const p = new URLSearchParams(params);
    if (v === null || (k === 'tab' && v === 'overview')) p.delete(k);
    else p.set(k, v);
    setParams(p, { replace: true });
  };
  const years = o ? [...new Set([...o.years, Number(o.today.slice(0, 4))])].sort((a, b) => b - a) : [];
  return (
    <Shell>
      <div className="page reports es-page">
        <div className="page-head">
          <div>
            <h1>{t('esg.title')}</h1>
            <p className="muted">{t('esg.lead')}</p>
          </div>
          {o ? (
            <span className="es-head-actions">
              <label className="es-year">
                <span className="muted small">{t('esg.year')}</span>
                <select className="input" value={o.year} onChange={(e) => set('year', e.target.value)}>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
              {tab === 'overview' ? (
                <Button icon="printer" onClick={() => window.print()}>
                  {t('esg.print')}
                </Button>
              ) : null}
            </span>
          ) : null}
        </div>
        <div className="segmented tabs" role="tablist" aria-label={t('esg.sections')}>
          {tabs.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set('tab', x.key)}>
              <Icon name={x.icon} size={16} />
              {t(`esg.tab_${x.key}` as MessageKey)}
            </button>
          ))}
        </div>
        {!o ? (
          <Skeleton h={360} r="var(--radius-lg)" />
        ) : tab === 'overview' ? (
          <OverviewTab o={o} manage={manage} go={(k) => set('tab', k)} />
        ) : tab === 'activities' ? (
          <ActivitiesTab o={o} manage={manage} onChange={reload} />
        ) : tab === 'factors' ? (
          <FactorsTab year={o.year} manage={manage} onChange={reload} />
        ) : tab === 'targets' ? (
          <TargetsTab o={o} manage={manage} onChange={reload} />
        ) : (
          <FiguresTab year={o.year} manage={manage} onChange={reload} />
        )}
      </div>
    </Shell>
  );
}

// ---- overview -------------------------------------------------------------------------------

function OverviewTab({ o, manage, go }: { o: Overview; manage: boolean; go: (k: Tab) => void }) {
  if (!o.count && !o.previous)
    return (
      <section className="panel">
        <EmptyState
          icon="leaf"
          title={t('esg.empty_title')}
          body={o.factors ? t('esg.empty_body_factors') : t('esg.empty_body')}
          action={manage ? <Button icon="plus" onClick={() => go(o.factors ? 'activities' : 'factors')}>{o.factors ? t('esg.add_activity') : t('esg.add_factor')}</Button> : undefined}
        />
      </section>
    );
  const change = o.previous ? Math.round(((o.total - o.previous) / o.previous) * 100) : null;
  const cats = (Object.entries(o.categories) as [Category, { grams: number; scope: Scope }][]).sort((a, b) => b[1].grams - a[1].grams);
  const thisYear = o.year === Number(o.today.slice(0, 4));
  return (
    <>
      <div className="rep-kpis">
        <Kpi
          label={t('esg.k_total', { year: o.year })}
          value={co2(o.total)}
          hint={o.fraction > 0 && o.fraction < 1 ? t('esg.heading_for', { amount: co2(Math.round(o.total / o.fraction)) }) : change === null ? t('esg.no_previous') : change === 0 ? t('esg.same_as', { year: o.year - 1 }) : change < 0 ? t('esg.less_than', { n: -change, year: o.year - 1 }) : t('esg.more_than', { n: change, year: o.year - 1 })}
          warn={change !== null && change > 0 && !thisYear}
        />
        {SCOPES.map((s) => (
          <Kpi key={s} label={scopeLabel(s)} value={co2(o.scopes[s])} hint={t('esg.share', { n: pct(o.scopes[s], o.total) })} />
        ))}
      </div>
      {thisYear ? (
        <p className="callout callout-info es-note">
          <Icon name="info" size={18} />
          <span>{t('esg.year_so_far', { date: day(o.today) })}</span>
        </p>
      ) : null}
      <div className="es-grid">
        <section className="panel es-wide">
          <div className="es-panel-head">
            <h2>{t('esg.by_month')}</h2>
            <span className="es-legend">
              {SCOPES.map((s) => (
                <span key={s}>
                  <span className={`es-swatch es-s${s}`} aria-hidden="true" />
                  {scopeLabel(s)}
                </span>
              ))}
            </span>
          </div>
          <StackedMonths months={o.months} />
        </section>
        <section className="panel">
          <h2 className="es-h2">{t('esg.by_category')}</h2>
          <div className="es-split" aria-label={t('esg.scope_split')} role="img">
            {SCOPES.map((s) => (o.scopes[s] ? <span key={s} className={`es-s${s}`} style={{ flexGrow: o.scopes[s] }} title={`${scopeLabel(s)} · ${co2(o.scopes[s])}`} /> : null))}
          </div>
          <ul className="es-cats">
            {cats.map(([c, v]) => (
              <li key={c}>
                <span className="es-cat-icon" aria-hidden="true">
                  <Icon name={CAT_ICON[c]} size={16} />
                </span>
                <span className="es-cat-name">
                  {catLabel(c)}
                  <ScopeChip scope={v.scope} />
                </span>
                <span className="es-cat-bar" aria-hidden="true">
                  <span className={`es-s${v.scope}`} style={{ width: `${Math.max(2, pct(v.grams, cats[0]![1].grams))}%` }} />
                </span>
                <span className="es-num">{co2(v.grams)}</span>
                <span className="es-num muted">{pct(v.grams, o.total)}%</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="panel">
          <h2 className="es-h2">{t('esg.intensity')}</h2>
          <dl className="es-facts">
            <div>
              <dt>{t('esg.per_person')}</dt>
              <dd>{o.intensity.per_person === null ? <span className="muted">{t('esg.per_person_none')}</span> : co2(o.intensity.per_person)}</dd>
            </div>
            <div>
              <dt>{t('esg.per_million', { amount: money(100_000_000) })}</dt>
              <dd>{o.intensity.per_million === null ? <span className="muted">{t('esg.per_million_none')}</span> : co2(o.intensity.per_million)}</dd>
            </div>
            <div>
              <dt>{t('esg.activities_n')}</dt>
              <dd>{num(o.count, 0)}</dd>
            </div>
            <div>
              <dt>{t('esg.figures_filled')}</dt>
              <dd>
                <button type="button" className="es-link" onClick={() => go('figures')}>
                  {t('esg.n_of_m', { n: o.metrics.filled, m: o.metrics.total })}
                </button>
              </dd>
            </div>
          </dl>
        </section>
        <section className="panel es-wide">
          <div className="es-panel-head">
            <h2>{t('esg.targets')}</h2>
            <button type="button" className="es-link" onClick={() => go('targets')}>
              {t('esg.see_targets')}
            </button>
          </div>
          {!o.targets.length ? <p className="muted">{t('esg.no_targets_short')}</p> : <TargetList o={o} />}
        </section>
      </div>
      <p className="muted small es-footnote">{t('esg.footnote')}</p>
    </>
  );
}

function StackedMonths({ months }: { months: Overview['months'] }) {
  const max = Math.max(1, ...months.map((m) => m['1'] + m['2'] + m['3']));
  const fmt = new Intl.DateTimeFormat(locale(), { month: 'short' });
  return (
    <figure className="es-months">
      <div className="es-months-plot">
        {months.map((m) => {
          const total = m['1'] + m['2'] + m['3'];
          const label = fmt.format(new Date(Date.UTC(2020, m.month - 1, 1)));
          return (
            <span key={m.month} className="es-month" title={`${label}: ${co2(total)}`}>
              <span className="es-stack" style={{ height: `${(total / max) * 100}%` }}>
                {SCOPES.map((s) => (m[s] ? <span key={s} className={`es-s${s}`} style={{ flexGrow: m[s] }} /> : null))}
              </span>
              <span className="es-month-label">{label}</span>
            </span>
          );
        })}
      </div>
      <figcaption className="sr-only">{months.map((m) => `${m.month}: ${co2(m['1'] + m['2'] + m['3'])}`).join(', ')}</figcaption>
    </figure>
  );
}

function TargetList({ o, onEdit }: { o: Overview; onEdit?: (t: Target) => void }) {
  return (
    <ul className="es-targets">
      {o.targets.map((x) => {
        const done = x.base && x.projected !== null ? Math.min(100, Math.max(0, Math.round(((x.base - x.projected) / (x.base - x.goal || 1)) * 100))) : 0;
        const running = o.fraction < 1;
        const state: { tone: Tone; icon: IconName; label: string } =
          x.on_track === null ? { tone: 'neutral', icon: 'info', label: t('esg.no_base', { year: x.base_year }) } : x.on_track ? { tone: 'ok', icon: 'check', label: t('esg.on_track') } : { tone: 'warn', icon: 'alert', label: t('esg.behind') };
        return (
          <li key={x.id}>
            <div className="es-target-head">
              <div>
                <strong>{x.name}</strong>
                <span className="muted small">
                  {t('esg.target_line', { scope: t(`esg.ts_${x.scope}` as MessageKey), n: num(x.reduction / 100), base: x.base_year, year: x.target_year })}
                </span>
              </div>
              <span className="es-target-actions">
                <StatusBadge tone={state.tone} icon={state.icon} label={state.label} />
                {onEdit ? (
                  <Button size="sm" variant="ghost" icon="edit" onClick={() => onEdit(x)}>
                    {t('esg.edit')}
                  </Button>
                ) : null}
              </span>
            </div>
            <div className="es-progress" role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={100} aria-label={t('esg.progress_of', { name: x.name })}>
              <span className={x.on_track === false ? 'behind' : ''} style={{ width: `${done}%` }} />
            </div>
            <dl className="es-target-facts">
              <div>
                <dt>{t('esg.base', { year: x.base_year })}</dt>
                <dd>{co2(x.base)}</dd>
              </div>
              <div>
                <dt>{running ? t('esg.so_far', { year: o.year }) : t('esg.this_year', { year: o.year })}</dt>
                <dd>
                  {co2(x.current)}
                  {running && x.projected !== null ? <span className="es-dd-sub">{t('esg.heading_for', { amount: co2(x.projected) })}</span> : null}
                </dd>
              </div>
              <div>
                <dt>{t('esg.on_path', { year: o.year })}</dt>
                <dd>{co2(x.path)}</dd>
              </div>
              <div>
                <dt>{t('esg.goal', { year: x.target_year })}</dt>
                <dd>{co2(x.goal)}</dd>
              </div>
            </dl>
          </li>
        );
      })}
    </ul>
  );
}

// ---- activities -----------------------------------------------------------------------------

function ActivitiesTab({ o, manage, onChange }: { o: Overview; manage: boolean; onChange: () => Promise<unknown> }) {
  const [scope, setScope] = useState<Scope | null>(null);
  const [category, setCategory] = useState<Category | ''>('');
  const [q, setQ] = useState('');
  const { data, reload } = useAction<{ rows: ActivityRow[]; more: boolean }>('esg.activities', { year: o.year, scope, category: category || null, q: q.trim() || null });
  const { data: fs, reload: reloadFactors } = useAction<Factor[]>('esg.factors');
  const [edit, setEdit] = useState<ActivityRow | 'new' | null>(null);
  const [imp, setImp] = useState(false);
  const [fleet, setFleet] = useState(false);
  const changed = async () => {
    await Promise.all([reload(), onChange(), reloadFactors()]);
  };
  const total = (data?.rows ?? []).reduce((a, r) => a + r.grams, 0);
  return (
    <section className="panel">
      <div className="es-panel-head">
        <div>
          <h2>{t('esg.activities')}</h2>
          <p className="muted small">{t('esg.activities_lead')}</p>
        </div>
        <span className="es-row-actions">
          {manage && o.fleet ? (
            <Button icon="car" onClick={() => setFleet(true)}>
              {t('esg.from_fleet')}
            </Button>
          ) : null}
          {manage ? (
            <Button icon="upload" onClick={() => setImp(true)}>
              {t('esg.import')}
            </Button>
          ) : null}
          {data?.rows.length ? (
            <Button
              icon="download"
              variant="ghost"
              onClick={() =>
                downloadCsv(
                  `emissions-${o.year}.csv`,
                  [t('esg.date'), t('esg.factor'), t('esg.scope'), t('esg.quantity'), t('esg.unit'), t('esg.kg_co2e'), t('esg.note')],
                  data.rows.map((r) => [r.date, r.factor_name, r.scope, (r.quantity / 1000).toFixed(3), r.unit, (r.grams / 1000).toFixed(3), r.note ?? '']),
                )
              }
            >
              {t('esg.csv')}
            </Button>
          ) : null}
          {manage ? (
            <Button variant="primary" icon="plus" disabled={!fs?.some((f) => f.active)} onClick={() => setEdit('new')}>
              {t('esg.add_activity')}
            </Button>
          ) : null}
        </span>
      </div>
      <div className="es-filters">
        <div className="segmented" role="group" aria-label={t('esg.scope')}>
          <button type="button" aria-pressed={scope === null} className={scope === null ? 'active' : ''} onClick={() => setScope(null)}>
            {t('esg.all_scopes')}
          </button>
          {SCOPES.map((s) => (
            <button key={s} type="button" aria-pressed={scope === s} className={scope === s ? 'active' : ''} onClick={() => setScope(s)}>
              {t('esg.scope_n', { n: s })}
            </button>
          ))}
        </div>
        <select className="input" aria-label={t('esg.category')} value={category} onChange={(e) => setCategory(e.target.value as Category | '')}>
          <option value="">{t('esg.all_categories')}</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {catLabel(c)}
            </option>
          ))}
        </select>
        <input className="input es-search" type="search" value={q} placeholder={t('esg.search')} aria-label={t('esg.search')} onChange={(e) => setQ(e.target.value)} />
      </div>
      {!data || !fs ? (
        <Skeleton h={240} />
      ) : !fs.length ? (
        <EmptyState icon="sliders" title={t('esg.no_factors')} body={t('esg.no_factors_body')} />
      ) : !data.rows.length ? (
        <EmptyState icon="leaf" title={t('esg.no_activities', { year: o.year })} body={t('esg.no_activities_body')} />
      ) : (
        <>
          <Table
            head={[t('esg.date'), t('esg.factor'), t('esg.quantity'), t('esg.emissions'), t('esg.note')]}
            left={[1, 4]}
            phoneHide={[0, 4]}
            rows={data.rows.map((r) => ({
              key: r.id,
              onClick: manage ? () => setEdit(r) : undefined,
              cells: [
                day(r.date),
                <span className="es-factor-cell">
                  <span>{r.factor_name}</span>
                  <span className="es-sub">
                    <ScopeChip scope={r.scope} />
                    <span className="muted small">{r.source === 'manual' ? (r.by_name ?? '') : t(`esg.src_${r.source}` as MessageKey)}</span>
                  </span>
                </span>,
                `${qtyText(r.quantity)} ${r.unit}`,
                <strong>{co2(r.grams)}</strong>,
                r.note ?? '',
              ],
            }))}
            foot={[t('esg.shown_total', { n: data.rows.length }), '', '', co2(total), '']}
          />
          {data.more ? <p className="muted small">{t('esg.more_rows')}</p> : null}
        </>
      )}
      {edit && fs ? <ActivityDialog row={edit === 'new' ? null : edit} factors={fs} year={o.year} today={o.today} onClose={() => setEdit(null)} onSaved={changed} /> : null}
      {imp ? <ImportDialog onClose={() => setImp(false)} onDone={changed} /> : null}
      {fleet && fs ? <FleetDialog year={o.year} factors={fs} onClose={() => setFleet(false)} onDone={changed} /> : null}
    </section>
  );
}

function ActivityDialog({ row, factors, year, today, onClose, onSaved }: { row: ActivityRow | null; factors: Factor[]; year: number; today: string; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const usable = factors.filter((f) => f.active || f.id === row?.factor);
  const [f, setF] = useState({
    date: row?.date ?? (today.startsWith(String(year)) ? today : `${year}-12-31`),
    factor: row?.factor ?? usable[0]?.id ?? '',
    quantity: row ? String(row.quantity / 1000) : '',
    note: row?.note ?? '',
  });
  const [drop, setDrop] = useState(false);
  const factor = usable.find((x) => x.id === f.factor);
  const q = parseDecimal(f.quantity);
  const mg = row && row.factor === f.factor ? row.mg_per_unit : (factor?.mg_per_unit ?? 0);
  const grams = q ? Math.round((q * mg) / 1_000_000) : null;
  return (
    <Dialog
      title={row ? t('esg.edit_activity') : t('esg.add_activity')}
      onClose={onClose}
      footer={
        <>
          {row ? (
            <Button variant="ghost" icon="trash" className="es-left" onClick={() => setDrop(true)}>
              {t('esg.delete')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!q || !f.factor || !f.date}
            onClick={() =>
              void run(async () => {
                await action('esg.save_activity', { id: row?.id ?? null, date: f.date, factor: f.factor, quantity: q, note: f.note || null });
                await onSaved();
                onClose();
              }, t('esg.activity_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('esg.factor')} required>
          <select className="input" value={f.factor} onChange={(e) => setF({ ...f, factor: e.target.value })}>
            {SCOPES.map((s) => (
              <optgroup key={s} label={scopeLabel(s)}>
                {usable
                  .filter((x) => x.scope === s)
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </Field>
        <Field label={t('esg.date')} required>
          <input className="input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        </Field>
        <Field label={factor ? t('esg.quantity_in', { unit: factor.unit }) : t('esg.quantity')} error={f.quantity && !q ? t('esg.number_bad') : undefined} required>
          <input className="input" inputMode="decimal" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} />
        </Field>
        <Field label={t('esg.note')}>
          <input className="input" value={f.note} maxLength={200} placeholder={t('esg.note_ph')} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
      </div>
      <div className="es-preview" aria-live="polite">
        <Icon name="leaf" size={18} />
        <span>{grams === null ? t('esg.preview_empty') : t('esg.preview', { amount: co2(grams), factor: num(mg / 1_000_000, 6), unit: factor?.unit ?? '' })}</span>
      </div>
      {row && row.factor === f.factor && factor && row.mg_per_unit !== factor.mg_per_unit ? <p className="muted small">{t('esg.kept_factor')}</p> : null}
      {drop && row ? (
        <Confirm
          title={t('esg.delete_activity_title')}
          body={t('esg.delete_activity_body', { amount: co2(row.grams) })}
          confirm={t('esg.delete')}
          danger
          onClose={() => setDrop(false)}
          onConfirm={() =>
            void run(async () => {
              await action('esg.delete_activity', { id: row.id });
              await onSaved();
              onClose();
            }, t('esg.activity_deleted'))
          }
        />
      ) : null}
    </Dialog>
  );
}

function ImportDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const file = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [check, setCheck] = useState<{ rows: number; grams: number } | null>(null);
  const pick = async (f: File) => {
    setName(f.name);
    setText(await f.text());
    setCheck(null);
  };
  return (
    <Dialog
      title={t('esg.import_title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {check ? (
            <Button
              variant="primary"
              icon="check"
              busy={busy}
              onClick={() =>
                void run(async () => {
                  await upload('esg.import', { text });
                  await onDone();
                  onClose();
                }, tn('esg.imported', check.rows))
              }
            >
              {tn('esg.import_n', check.rows)}
            </Button>
          ) : (
            <Button
              variant="primary"
              icon="search"
              busy={busy}
              disabled={!text.trim()}
              onClick={() =>
                void run(async () => {
                  setCheck(await upload<{ rows: number; grams: number }>('esg.import', { text, dry: true }));
                })
              }
            >
              {t('esg.check_file')}
            </Button>
          )}
        </>
      }
    >
      <div className="form-stack">
        <p className="muted small">{t('esg.import_lead')}</p>
        <pre className="es-sample">{'date;factor;quantity;note\n31.01.2026;Diesel;420;Delivery van\n31.01.2026;Grid electricity;5 200;January bill'}</pre>
        <div className="es-row-actions">
          <Button icon="file" onClick={() => file.current?.click()}>
            {name ? t('esg.other_file') : t('esg.choose_file')}
          </Button>
          {name ? <span className="muted small">{name}</span> : null}
          <input
            ref={file}
            type="file"
            accept=".csv,.txt,text/csv"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void pick(f);
            }}
          />
        </div>
        <Field label={t('esg.or_paste')}>
          <textarea
            className="input"
            rows={5}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setName('');
              setCheck(null);
            }}
          />
        </Field>
        {check ? (
          <p className="callout callout-ok">
            <Icon name="check" size={18} />
            <span>{tn('esg.check_ok', check.rows, { amount: co2(check.grams) })}</span>
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

function FleetDialog({ year, factors, onClose, onDone }: { year: number; factors: Factor[]; onClose: () => void; onDone: () => Promise<unknown> }) {
  const { data } = useAction<{ fuel: string; month: string; quantity: number; fills: number; done: boolean }[]>('esg.fleet_fuel', { year });
  const { busy, run } = useSave();
  const fuelFactors = factors.filter((f) => f.active && f.category === 'fuel');
  const todo = (data ?? []).filter((r) => !r.done);
  const kinds = [...new Set(todo.map((r) => r.fuel))];
  const guess = (fuel: string) => fuelFactors.find((f) => f.name.toLowerCase().includes(fuel === 'lpg' ? 'lpg' : fuel === 'diesel' ? 'diesel' : 'petrol'))?.id ?? fuelFactors[0]?.id ?? '';
  const [map, setMap] = useState<Record<string, string>>({});
  const value = (fuel: string) => map[fuel] ?? guess(fuel);
  const fmt = new Intl.DateTimeFormat(locale(), { month: 'long' });
  return (
    <Dialog
      title={t('esg.fleet_title', { year })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!todo.length || kinds.some((k) => !value(k))}
            onClick={() =>
              void run(async () => {
                await action('esg.import_fleet', { year, factors: Object.fromEntries(kinds.map((k) => [k, value(k)])) });
                await onDone();
                onClose();
              }, tn('esg.fleet_done', todo.length))
            }
          >
            {tn('esg.fleet_bring', todo.length)}
          </Button>
        </>
      }
    >
      {!data ? (
        <Skeleton h={160} />
      ) : !data.length ? (
        <EmptyState icon="car" title={t('esg.fleet_none')} body={t('esg.fleet_none_body')} />
      ) : (
        <div className="form-stack">
          <p className="muted small">{t('esg.fleet_lead')}</p>
          {!fuelFactors.length ? <p className="callout callout-warn">{t('esg.fleet_no_factor')}</p> : null}
          {kinds.map((k) => (
            <Field key={k} label={t('esg.fleet_factor_for', { fuel: t(`esg.fuel_${k}` as MessageKey) })}>
              <select className="input" value={value(k)} onChange={(e) => setMap({ ...map, [k]: e.target.value })}>
                {fuelFactors.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name} · {num(f.mg_per_unit / 1_000_000, 4)} kg/{f.unit}
                  </option>
                ))}
              </select>
            </Field>
          ))}
          <Table
            head={[t('esg.month'), t('esg.fuel'), t('esg.litres'), '']}
            left={[1]}
            rows={data.map((r) => ({
              key: r.month + r.fuel,
              cells: [
                fmt.format(new Date(`${r.month}-15T00:00:00Z`)),
                t(`esg.fuel_${r.fuel}` as MessageKey),
                qtyText(r.quantity),
                r.done ? <StatusBadge tone="ok" icon="check" label={t('esg.already_in')} /> : <StatusBadge tone="info" icon="plus" label={t('esg.to_bring')} />,
              ],
            }))}
          />
        </div>
      )}
    </Dialog>
  );
}

// ---- factors --------------------------------------------------------------------------------

function FactorsTab({ year, manage, onChange }: { year: number; manage: boolean; onChange: () => Promise<unknown> }) {
  const { data, reload } = useAction<Factor[]>('esg.factors');
  const [edit, setEdit] = useState<Factor | 'new' | null>(null);
  const changed = async () => {
    await Promise.all([reload(), onChange()]);
  };
  return (
    <section className="panel">
      <div className="es-panel-head">
        <div>
          <h2>{t('esg.factors')}</h2>
          <p className="muted small">{t('esg.factors_lead')}</p>
        </div>
        {manage ? (
          <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
            {t('esg.add_factor')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : !data.length ? (
        <EmptyState icon="sliders" title={t('esg.no_factors')} body={t('esg.no_factors_body')} />
      ) : (
        <Table
          head={[t('esg.factor'), t('esg.scope'), t('esg.category'), t('esg.kg_per_unit'), t('esg.source'), t('esg.used')]}
          left={[1, 2, 4]}
          phoneHide={[1, 2, 4, 5]}
          rows={data.map((f) => ({
            key: f.id,
            onClick: manage ? () => setEdit(f) : undefined,
            cells: [
              <span className="es-factor-cell">
                <strong>{f.name}</strong>
                {!f.active ? <span className="muted small">{t('esg.off')}</span> : null}
              </span>,
              <ScopeChip scope={f.scope} />,
              catLabel(f.category),
              t('esg.kg_per', { n: num(f.mg_per_unit / 1_000_000, 6), unit: f.unit }),
              <span className="es-source">{f.source ?? '—'}</span>,
              String(f.used),
            ],
          }))}
        />
      )}
      {edit ? <FactorDialog factor={edit === 'new' ? null : edit} year={year} onClose={() => setEdit(null)} onSaved={changed} /> : null}
    </section>
  );
}

function FactorDialog({ factor, year, onClose, onSaved }: { factor: Factor | null; year: number; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: factor?.name ?? '',
    category: factor?.category ?? ('fuel' as Category),
    scope: factor?.scope ?? ('1' as Scope),
    unit: factor?.unit ?? '',
    kg: factor ? String(factor.mg_per_unit / 1_000_000) : '',
    source: factor?.source ?? '',
    active: factor?.active ?? true,
  });
  const [drop, setDrop] = useState(false);
  const [reapply, setReapply] = useState(false);
  const mg = parseDecimal(f.kg, 6);
  const changedValue = !!factor && mg !== null && mg !== factor.mg_per_unit && factor.used > 0;
  return (
    <Dialog
      title={factor ? t('esg.edit_factor') : t('esg.add_factor')}
      onClose={onClose}
      footer={
        <>
          {factor && !factor.used ? (
            <Button variant="ghost" icon="trash" className="es-left" onClick={() => setDrop(true)}>
              {t('esg.delete')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!f.name.trim() || !f.unit.trim() || !mg}
            onClick={() =>
              void run(async () => {
                const r = await action<{ id: string }>('esg.save_factor', { id: factor?.id ?? null, ...f, mg_per_unit: mg, source: f.source || null });
                if (changedValue && reapply) await action('esg.reapply_factor', { id: r.id, year });
                await onSaved();
                onClose();
              }, t('esg.factor_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('esg.factor_name')} required>
          <input className="input" value={f.name} maxLength={120} placeholder={t('esg.factor_name_ph')} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={t('esg.category')}>
          <select className="input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value as Category, scope: factor ? f.scope : DEFAULT_SCOPE[e.target.value as Category] })}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {catLabel(c)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('esg.unit')} hint={t('esg.unit_hint')} required>
          <input className="input" value={f.unit} maxLength={20} placeholder={t('esg.unit_ph')} onChange={(e) => setF({ ...f, unit: e.target.value })} />
        </Field>
        <Field label={t('esg.kg_per_unit')} hint={t('esg.kg_hint')} error={f.kg && !mg ? t('esg.number_bad') : undefined} required>
          <input className="input" inputMode="decimal" value={f.kg} onChange={(e) => setF({ ...f, kg: e.target.value })} />
        </Field>
      </div>
      <fieldset className="es-scopes">
        <legend>{t('esg.scope')}</legend>
        {SCOPES.map((s) => (
          <label key={s} className={`es-scope-choice${f.scope === s ? ' on' : ''}`}>
            <input type="radio" name="scope" checked={f.scope === s} onChange={() => setF({ ...f, scope: s })} />
            <span>
              <strong>{scopeLabel(s)}</strong>
              <span className="muted small">{t(`esg.scope_${s}_hint` as MessageKey)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <div className="form-stack">
        <Field label={t('esg.source')} hint={t('esg.source_hint')}>
          <input className="input" value={f.source} maxLength={200} onChange={(e) => setF({ ...f, source: e.target.value })} />
        </Field>
        {factor ? (
          <label className="check-row">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
            <span>{t('esg.factor_active')}</span>
          </label>
        ) : null}
        {changedValue ? (
          <label className="check-row">
            <input type="checkbox" checked={reapply} onChange={(e) => setReapply(e.target.checked)} />
            <span>{t('esg.reapply', { year })}</span>
          </label>
        ) : null}
      </div>
      {drop && factor ? (
        <Confirm
          title={t('esg.delete_factor_title', { name: factor.name })}
          body={t('esg.delete_factor_body')}
          confirm={t('esg.delete')}
          danger
          onClose={() => setDrop(false)}
          onConfirm={() =>
            void run(async () => {
              await action('esg.delete_factor', { id: factor.id });
              await onSaved();
              onClose();
            }, t('esg.factor_deleted'))
          }
        />
      ) : null}
    </Dialog>
  );
}

// ---- targets --------------------------------------------------------------------------------

function TargetsTab({ o, manage, onChange }: { o: Overview; manage: boolean; onChange: () => Promise<unknown> }) {
  const [edit, setEdit] = useState<Target | 'new' | null>(null);
  return (
    <section className="panel">
      <div className="es-panel-head">
        <div>
          <h2>{t('esg.targets')}</h2>
          <p className="muted small">{t('esg.targets_lead')}</p>
        </div>
        {manage ? (
          <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
            {t('esg.add_target')}
          </Button>
        ) : null}
      </div>
      {!o.targets.length ? <EmptyState icon="flame" title={t('esg.no_targets')} body={t('esg.no_targets_body')} /> : <TargetList o={o} onEdit={manage ? (x) => setEdit(x) : undefined} />}
      {edit ? <TargetDialog target={edit === 'new' ? null : edit} year={o.year} onClose={() => setEdit(null)} onSaved={onChange} /> : null}
    </section>
  );
}

function TargetDialog({ target, year, onClose, onSaved }: { target: Target | null; year: number; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: target?.name ?? '',
    scope: target?.scope ?? ('all' as TargetScope),
    base_year: String(target?.base_year ?? year - 1),
    target_year: String(target?.target_year ?? year + 4),
    reduction: target ? String(target.reduction / 100) : '30',
  });
  const [drop, setDrop] = useState(false);
  const red = parseDecimal(f.reduction, 2);
  const by = Number(f.base_year);
  const ty = Number(f.target_year);
  const ok = f.name.trim() && red && red > 0 && red <= 10_000 && by >= 2000 && ty > by && ty <= 2100;
  return (
    <Dialog
      title={target ? t('esg.edit_target') : t('esg.add_target')}
      size="sm"
      onClose={onClose}
      footer={
        <>
          {target ? (
            <Button variant="ghost" icon="trash" className="es-left" onClick={() => setDrop(true)}>
              {t('esg.delete')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!ok}
            onClick={() =>
              void run(async () => {
                await action('esg.save_target', { id: target?.id ?? null, name: f.name, scope: f.scope, base_year: by, target_year: ty, reduction: red });
                await onSaved();
                onClose();
              }, t('esg.target_saved'))
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('esg.target_name')} required>
          <input className="input" value={f.name} maxLength={120} placeholder={t('esg.target_name_ph')} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={t('esg.target_scope')}>
          <select className="input" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value as TargetScope })}>
            {TARGET_SCOPES.map((s) => (
              <option key={s} value={s}>
                {t(`esg.ts_${s}` as MessageKey)}
              </option>
            ))}
          </select>
        </Field>
        <div className="form-grid">
          <Field label={t('esg.base_year')} required>
            <input className="input" inputMode="numeric" value={f.base_year} onChange={(e) => setF({ ...f, base_year: e.target.value })} />
          </Field>
          <Field label={t('esg.target_year')} error={ty && by && ty <= by ? t('esg.year_after') : undefined} required>
            <input className="input" inputMode="numeric" value={f.target_year} onChange={(e) => setF({ ...f, target_year: e.target.value })} />
          </Field>
        </div>
        <Field label={t('esg.reduction_pct')} error={f.reduction && (!red || red > 10_000) ? t('esg.reduction_bad') : undefined} required>
          <input className="input" inputMode="decimal" value={f.reduction} onChange={(e) => setF({ ...f, reduction: e.target.value })} />
        </Field>
      </div>
      {drop && target ? (
        <Confirm
          title={t('esg.delete_target_title', { name: target.name })}
          body={t('esg.delete_target_body')}
          confirm={t('esg.delete')}
          danger
          onClose={() => setDrop(false)}
          onConfirm={() =>
            void run(async () => {
              await action('esg.delete_target', { id: target.id });
              await onSaved();
              onClose();
            }, t('esg.target_deleted'))
          }
        />
      ) : null}
    </Dialog>
  );
}

// ---- figures --------------------------------------------------------------------------------

const PILLARS: { key: Pillar; icon: IconName }[] = [
  { key: 'environment', icon: 'leaf' },
  { key: 'social', icon: 'users' },
  { key: 'governance', icon: 'shield' },
];

function FiguresTab({ year, manage, onChange }: { year: number; manage: boolean; onChange: () => Promise<unknown> }) {
  const { data, reload } = useAction<Metric[]>('esg.metrics', { year });
  const { busy, run } = useSave();
  const [draft, setDraft] = useState<Record<string, { value: string; note: string }>>({});
  const entry = (m: Metric) => draft[m.key] ?? { value: m.value === null ? '' : String(m.value / 1000), note: m.note ?? '' };
  const dirty = useMemo(
    () =>
      (data ?? []).filter((m) => {
        const d = draft[m.key];
        if (!d) return false;
        const v = d.value.trim() ? parseDecimal(d.value) : null;
        return v !== m.value || (d.note || null) !== m.note;
      }),
    [data, draft],
  );
  const invalid = (data ?? []).some((m) => {
    const d = draft[m.key];
    if (!d?.value.trim()) return false;
    const v = parseDecimal(d.value);
    return v === null || (m.unit === '%' && v > 100_000);
  });
  return (
    <>
      <div className="es-panel-head es-figures-head">
        <p className="muted">{t('esg.figures_lead', { year })}</p>
        {manage ? (
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!dirty.length || invalid}
            onClick={() =>
              void run(async () => {
                await action('esg.save_metrics', {
                  year,
                  items: dirty.map((m) => {
                    const d = draft[m.key]!;
                    return { key: m.key, value: d.value.trim() ? parseDecimal(d.value) : null, note: d.note || null };
                  }),
                });
                setDraft({});
                await Promise.all([reload(), onChange()]);
              }, t('esg.figures_saved'))
            }
          >
            {dirty.length ? tn('esg.save_n', dirty.length) : t('common.save')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={360} r="var(--radius-lg)" />
      ) : (
        <div className="es-pillars">
          {PILLARS.map((p) => (
            <section key={p.key} className="panel">
              <h2 className="es-h2 es-pillar-title">
                <span className={`es-pillar-icon es-p-${p.key}`} aria-hidden="true">
                  <Icon name={p.icon} size={18} />
                </span>
                {t(`esg.pillar_${p.key}` as MessageKey)}
              </h2>
              <ul className="es-metrics">
                {data
                  .filter((m) => m.pillar === p.key)
                  .map((m) => {
                    const e = entry(m);
                    const v = e.value.trim() ? parseDecimal(e.value) : null;
                    const bad = !!e.value.trim() && (v === null || (m.unit === '%' && v > 100_000));
                    const id = `esg-m-${m.key}`;
                    return (
                      <li key={m.key}>
                        <label htmlFor={id} className="es-metric-label">
                          <span>{t(`esg.m_${m.key}` as MessageKey)}</span>
                          <span className="muted small">
                            {m.previous === null ? t('esg.prev_none', { year: year - 1 }) : t('esg.prev', { year: year - 1, value: `${num(m.previous / 1000, 3)}${m.unit ? ` ${m.unit}` : ''}` })}
                          </span>
                        </label>
                        <span className={`es-metric-input${bad ? ' bad' : ''}`}>
                          <input
                            id={id}
                            className="input"
                            inputMode="decimal"
                            value={e.value}
                            disabled={!manage}
                            aria-invalid={bad || undefined}
                            onChange={(x) => setDraft({ ...draft, [m.key]: { ...e, value: x.target.value } })}
                          />
                          {m.unit ? <span className="es-unit">{m.unit}</span> : null}
                        </span>
                        <input
                          className="input es-metric-note"
                          value={e.note}
                          maxLength={200}
                          disabled={!manage}
                          placeholder={t('esg.where_from')}
                          aria-label={t('esg.note_for', { name: t(`esg.m_${m.key}` as MessageKey) })}
                          onChange={(x) => setDraft({ ...draft, [m.key]: { ...e, note: x.target.value } })}
                        />
                      </li>
                    );
                  })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </>
  );
}
